import {
  LogoSharingInfo,
  OrganizationSharing,
  SharingChainShop,
  SharingRelation,
  SharingRelationMode,
  SharingRelationSource,
  SharingRelationStatus,
} from "../../../core/models/entities/LogoSharing";
import {
  LogoSharingInfoDto,
  OrganizationSharingDto,
  SharingChainShopDto,
  SharingRelationDto,
} from "../responses/LogoSharingDto";

// The API contract allows the enum-ish fields to arrive either as strings
// ("Open") or as raw numeric enum values (0/1). Normalize both here so the
// UI only ever deals with the string forms. Numeric order is pinned by the
// contract: status 0=Open/1=Closed, mode 0=AllInCategories/1=SelectedOnly,
// source 0=Auto/1=Manual.

export function mapSharingStatus(
  value: SharingRelationDto["status"] | null | undefined,
): SharingRelationStatus {
  if (value === "Closed" || value === 1) return "Closed";
  return "Open";
}

function mapSharingMode(
  value: SharingRelationDto["mode"] | null | undefined,
): SharingRelationMode {
  if (value === "SelectedOnly" || value === 1) return "SelectedOnly";
  return "AllInCategories";
}

function mapSharingSource(
  value: SharingRelationDto["source"] | null | undefined,
): SharingRelationSource {
  if (value === "Manual" || value === 1) return "Manual";
  return "Auto";
}

export function mapToSharingRelation(dto: SharingRelationDto): SharingRelation {
  return {
    id: dto.id,
    giverOrgId: dto.giverOrgId,
    giverOrgName: dto.giverOrgName ?? "",
    receiverOrgId: dto.receiverOrgId,
    receiverOrgName: dto.receiverOrgName ?? "",
    status: mapSharingStatus(dto.status),
    categories: dto.categories ?? [],
    mode: mapSharingMode(dto.mode),
    source: mapSharingSource(dto.source),
    selectedCount: dto.selectedCount ?? 0,
    updatedAtUtc: dto.updatedAtUtc ? new Date(dto.updatedAtUtc) : undefined,
  };
}

export function mapToOrganizationSharing(
  dto: OrganizationSharingDto,
): OrganizationSharing {
  return {
    given: (dto.given ?? []).map(mapToSharingRelation),
    received: (dto.received ?? []).map(mapToSharingRelation),
    suspended: dto.suspended ?? false,
    globalExclusion: dto.globalExclusion
      ? {
          excluded: dto.globalExclusion.excluded ?? false,
          categories: dto.globalExclusion.categories ?? [],
        }
      : null,
  };
}

export function mapToSharingChainShop(
  dto: SharingChainShopDto,
): SharingChainShop {
  return {
    orgId: dto.orgId,
    name: dto.name ?? "",
    navNumber: dto.navNumber ?? "",
    suspended: dto.suspended ?? false,
    relationStatus:
      dto.relationStatus === null || dto.relationStatus === undefined
        ? null
        : mapSharingStatus(dto.relationStatus),
    selectedCount: dto.selectedCount ?? 0,
    totalChainLogos: dto.totalChainLogos ?? 0,
  };
}

export function mapToLogoSharingInfo(dto: LogoSharingInfoDto): LogoSharingInfo {
  return {
    direct: dto.direct ?? [],
    viaRelations: dto.viaRelations ?? [],
    global: dto.global ?? false,
  };
}
